import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  addToast,
  useMobile,
} from '@octano/global-ui';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { useActionKitImp } from '../../../../components/ozmoit/common';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  getSchoolsListAction,
  getStudyPlanVersionsListAction,
} from '../actions';
import { ChildType } from '../types';

export interface ItemsFiltersValue {
  type: ChildType;
  schoolId?: number;
  studyPlanId?: number;
}

interface ItemsFiltersFormValues {
  type: SelectOptionType;
  school: SelectOptionType | null;
  studyPlan: SelectOptionType | null;
}

interface ItemsFiltersProps {
  onApply: (next: ItemsFiltersValue) => void;
}

/**
 * HU 15571 — Filtros para el buscador de actividades / sub-agrupaciones a
 * agregar a la Agrupación.
 *
 * Sigue el patrón de `parts/SearchFilters.tsx` (HU 15564): formulario con
 * react-hook-form, 3 filtros en md=3 (Tipo / Escuela / Plan de estudio) y
 * cuarto col md=3 con botones Buscar + Limpiar (flex-fill, size="md"). Los
 * filtros se aplican on-submit (no on-change) para evitar refetchs en cada
 * tick del usuario.
 *
 * - TIPO: requerido (default 'course'), no clearable.
 * - ESCUELA: opcional, isClearable.
 * - PLAN DE ESTUDIO: opcional, isClearable. Backend filter: el FE manda el
 *   `studyPlanId`; si el backend aún no lo cablea quedará como no-op
 *   inocuo hasta que se conecte.
 * - Limpiar: resetea a `{ type: 'course' }` y emite el reset hacia el padre.
 */
export default function ItemsFilters({
  onApply,
}: ItemsFiltersProps): ReactElement {
  const prefix = 'items.filters';
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);
  const isMobile = useMobile();
  const [schools, setSchools] = useState<SelectOptionType[]>([]);
  const [studyPlans, setStudyPlans] = useState<SelectOptionType[]>([]);

  const typeOptions: SelectOptionType[] = [
    { value: 'course', label: t(`${prefix}.typeCourse`) },
    { value: 'group', label: t(`${prefix}.typeGroup`) },
  ];

  const defaultValues: ItemsFiltersFormValues = {
    type: typeOptions[0],
    school: null,
    studyPlan: null,
  };

  const { control, handleSubmit, reset } = useForm<ItemsFiltersFormValues>({
    defaultValues,
  });

  const { action: loadSchools } = useActionKitImp(getSchoolsListAction, {
    onSuccess: (resp) => {
      setSchools(
        (resp?.data ?? []).map((school) => ({
          value: school.id,
          label: school.name,
        })),
      );
    },
    onError: {
      unknown: () => {
        addToast({
          color: 'danger',
          icon: 'error',
          text: t('errors.generic'),
        });
      },
    },
  });

  const { action: loadStudyPlans } = useActionKitImp(
    getStudyPlanVersionsListAction,
    {
      onSuccess: (resp) => {
        setStudyPlans(
          (resp?.studyPlanVersions ?? []).map((sp) => ({
            value: sp.id,
            label: sp.name,
          })),
        );
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            icon: 'error',
            text: t('errors.generic'),
          });
        },
      },
    },
  );

  useEffect(() => {
    loadSchools();
    loadStudyPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(
    (values: ItemsFiltersFormValues) => {
      onApply({
        type: (values.type?.value as ChildType) ?? 'course',
        schoolId:
          values.school && values.school.value !== undefined
            ? Number(values.school.value)
            : undefined,
        studyPlanId:
          values.studyPlan && values.studyPlan.value !== undefined
            ? Number(values.studyPlan.value)
            : undefined,
      });
    },
    [onApply],
  );

  const onSubmit = (values: ItemsFiltersFormValues): void => {
    emit(values);
  };

  const handleClear = useCallback(() => {
    reset(defaultValues);
    emit(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, emit]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="pb-3 gutters-filters">
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="type"
            label={t(`${prefix}.type`)}
            placeholder={t(`${prefix}.typePlaceholder`)}
            options={typeOptions}
            control={control}
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="school"
            label={t(`${prefix}.school`)}
            placeholder={t(`${prefix}.schoolPlaceholder`)}
            options={schools}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="studyPlan"
            label={t(`${prefix}.studyPlan`)}
            placeholder={t(`${prefix}.studyPlanPlaceholder`)}
            options={studyPlans}
            control={control}
            isClearable
          />
        </Col>
        <Col xs={12} md={3} style={{ marginTop: isMobile ? 0 : '0.5rem' }}>
          <div
            className="d-flex w-100 align-items-center h-100"
            style={{ gap: 8 }}
          >
            <Button
              type="submit"
              text={t('common:actions.search')}
              size="md"
              className="flex-fill"
            />
            <Button
              type="button"
              text={t('common:actions.clean')}
              size="md"
              outlined
              className="flex-fill"
              onClick={handleClear}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
}
